import React, { useState, useEffect, useRef } from "react";
import { InfoIconWithMessage } from "../../../shared/infoIcon/InfoIcon";
import { withTranslation } from "react-i18next";
import LeadTimesRow from "./LeadTimesRow";
import Grid from "@mui/material/Grid";
import styles from "./LeadTimes.module.scss";
import CheckBox from "../../../shared/checkBox/CheckBox";
import EditIcon from "../../../shared/Edit/EditIcon";
import HasPermissionTo from "../../../common/HasPermissionTo";
import LeadTimesClient from "../../../shared/clients/LeadTimesClient";
import {
  AT_YARD,
  LEAD_TIMES,
  ORDER_SCHEDULING,
  PRODUCED_IN_TRANSIT,
} from "../../../common/Constants";
import PendingActionClient from "../../../shared/clients/PendingActionClient";
import ValidationRules from "../../../common/ValidationRules";

function LeadTimes(props) {
  const [isClient, setIsClient] = useState(false);
  const [editAtYard, setEditAtYard] = useState(false);
  const [editProducedInTransit, setEditProducedInTransit] = useState(false);
  const [editOrderScheduling, setEditOrderScheduling] = useState(false);
  const [data, setData] = useState([]);
  const [firstLoad, setFirstLoad] = useState(false);
  const [totalChecked, setTotalChecked] = useState(0);
  const [editCount, setEditCount] = useState(0);
  const [buttonDisabled, setButtonDisabled] = useState(true);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [totalSaved, setTotalSaved] = useState(0);
  const [infoTooltipOpen, setInfoTooltipOpen] = useState(false);
  const [infoTooltipColumn, setInfoTooltipColumn] = useState("");

  const formRef = useRef(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsClient(true);
      ValidationRules();
      LeadTimesClient.getLeadTimes(props.user)
        .then((response) => {
          const firstLoadValue = !isNotFirstLoad(response);
          if (firstLoadValue) {
            response = response.map((d) => {
              d.checked = true;
              return d;
            });
          }
          setData(response);
          setFirstLoad(firstLoadValue);
          setTotalChecked(firstLoadValue ? response.length : 0);
        })
        .catch(() => {});
    }, 100);

    return () => clearTimeout(timer);
  }, []);

  function validateLeadTime(value) {
    if (!value || value.trim() === '') return false;

    const regex = /^[0-9][0-9]-[0-9][0-9]$/;
    if (!regex.test(value)) return false;

    const parts = value.split('-');
    const first = parseInt(parts[0]);
    const second = parseInt(parts[1]);

    return first < second;
  }

  function handleKeyDown(e) {
    if (e.keyCode === 39) {
      advanceFields(e);
    } else if (e.keyCode === 37) {
      goBack(e);
    }
  }

  function handleChange(e, index, key) {
    const dataCopy = JSON.parse(JSON.stringify(data));
    dataCopy[index]["deliveryStatus"][key] = e.target.value;

    const wasChecked = dataCopy[index].checked;
    !wasChecked && (dataCopy[index].checked = true);

    const newTotalChecked = wasChecked ? totalChecked : totalChecked + 1;

    setData(dataCopy);
    setTotalChecked(newTotalChecked);

    if (e.target.value.length === 5) {
      advanceFields(e);
    }
  }

  function handleEdit(key) {
    if (!isClient || typeof document === 'undefined') return;

    if (firstLoad && editCount === 0) {
      setInfoTooltipOpen(true);
      setInfoTooltipColumn(key);
    }

    if (saveSuccess) {
      const copyData = JSON.parse(JSON.stringify(data));
      copyData.forEach((item) => {
        item.saved = false;
      });
      setSaveSuccess(false);
      setData(copyData);
      setTotalSaved(0);
    }

    switch (key) {
      case AT_YARD:
        setEditAtYard(true);
        setEditCount(editCount + 1);
        break;
      case PRODUCED_IN_TRANSIT:
        setEditProducedInTransit(true);
        setEditCount(editCount + 1);
        break;
      case ORDER_SCHEDULING:
        setEditOrderScheduling(true);
        setEditCount(editCount + 1);
        break;
      default:
        return;
    }
  }

  useEffect(() => {
    if (isClient && data.length > 0) {
      if (firstLoad) {
        const allModesEnabled = editAtYard && editProducedInTransit && editOrderScheduling;
        if (!allModesEnabled) {
          setButtonDisabled(true);
          return;
        }

        const allFieldsValid = data.every(item => {
          if (!item.checked) return true;
          const { AtYard, ProducedInTransit, OrderScheduling } = item.deliveryStatus;
          return validateLeadTime(AtYard) && validateLeadTime(ProducedInTransit) && validateLeadTime(OrderScheduling);
        });

        setButtonDisabled(!allFieldsValid);
      } else {
        if (totalChecked === 0) {
          setButtonDisabled(true);
          return;
        }

        const checkedFieldsValid = data.every(item => {
          if (!item.checked) return true;
          const { AtYard, ProducedInTransit, OrderScheduling } = item.deliveryStatus;
          return validateLeadTime(AtYard) && validateLeadTime(ProducedInTransit) && validateLeadTime(OrderScheduling);
        });

        setButtonDisabled(!checkedFieldsValid);
      }
    }
  }, [data, editAtYard, editProducedInTransit, editOrderScheduling, totalChecked, firstLoad, isClient]);

  function handleSubmit(e) {
    e.preventDefault();

    const invalidFields = data.filter(item => {
      if (!item.checked) return false;
      const { AtYard, ProducedInTransit, OrderScheduling } = item.deliveryStatus;
      return !validateLeadTime(AtYard) || !validateLeadTime(ProducedInTransit) || !validateLeadTime(OrderScheduling);
    });

    if (invalidFields.length > 0) {
      console.error("Invalid lead times data:", invalidFields);
      alert("Please fix invalid lead time values before submitting");
      return;
    }

    const apiData = data
      .filter(item => item.checked)
      .map(item => {
        // eslint-disable-next-line no-unused-vars
        const { checked, saved, ...cleanItem } = item;
        return cleanItem;
      });

    const totalSavedValue = totalChecked;
    const clonedData = JSON.parse(JSON.stringify(data));
    clonedData.forEach((leadTime) => {
      if (leadTime.checked) {
        leadTime.saved = true;
        leadTime.checked = false;
      }
    });

    LeadTimesClient.postLeadTimes(apiData, props.user).then(() => {
      PendingActionClient.removeNscPendingAction(
        props.user,
        LEAD_TIMES,
        2
      );
      setEditAtYard(false);
      setEditProducedInTransit(false);
      setEditOrderScheduling(false);
      setData(clonedData);
      setFirstLoad(false);
      setTotalSaved(totalSavedValue);
      setTotalChecked(0);
      setEditCount(0);
      setButtonDisabled(true);
      setSaveSuccess(true);
    }).catch((error) => {
      console.error("Submit error:", error);
      console.error("Error response:", error.response?.data);
    });
  }

  function advanceFields(e) {
    const form = e.target.form;
    const index = Array.prototype.indexOf.call(form, e.target);
    let inputFound = false;
    let next;
    for (let i = index + 1; i < form.elements.length; ++i) {
      if (form.elements[i].type === "text") {
        inputFound = true;
        next = i;
        break;
      }
    }
    if (inputFound) {
      form.elements[next].focus();
      e.preventDefault();
    }
  }

  function goBack(e) {
    const form = e.target.form;
    const index = Array.prototype.indexOf.call(form, e.target);
    let inputFound = false;
    let prev;
    for (let i = index - 1; i >= 0; --i) {
      if (form.elements[i].type === "text") {
        inputFound = true;
        prev = i;
        break;
      }
    }
    if (inputFound) {
      form.elements[prev].focus();
      e.preventDefault();
    }
  }

  function isNotFirstLoad(leadTimes) {
    return leadTimes.every((lt) => {
      return (
        getTrimmedValue(lt.deliveryStatus.AtYard) &&
        getTrimmedValue(lt.deliveryStatus.ProducedInTransit) &&
        getTrimmedValue(lt.deliveryStatus.OrderScheduling)
      );
    });
  }

  function getTrimmedValue(value) {
    return value ? value.trim() : "";
  }

  function closeInfoTooltip() {
    setInfoTooltipOpen(false);
    setInfoTooltipColumn("");
  }

  if (!isClient) {
    return (
      <div className="pageWrapper">
        <div className="pagePaddingTop">
          <div className="pagePaddingLeft pagePaddingRight">
            <div>Loading...</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="pageWrapper">
      <div className="pagePaddingTop">
        <div className="pagePaddingLeft pagePaddingRight">
          <InfoIconWithMessage
            message={props.t("LeadTimes.subheader")}
          />
        </div>
        <div className="pagePaddingTop">
          <div>
            <Grid container spacing={0} className={styles.divider}>
              <Grid item xs={1} className={styles.checkboxHeader}>
                <div className={styles.checkbox}>
                  <CheckBox
                    id="leadTimesAll"
                    faded={true}
                    checked={totalChecked === data.length}
                    isDisabled={true}
                  />
                </div>
              </Grid>
              <Grid item xs={5} className={styles.descriptionHeader}>
                {props.t("LeadTimes.factoryNo")}
              </Grid>
              <Grid item xs={1} className={styles.editHeader}>
                <span
                  className={
                    !editAtYard
                      ? styles.negativeMarginTitle
                      : styles.title
                  }
                >
                  {props.t("LeadTimes.atYard")}
                </span>
                <HasPermissionTo
                  perform={["leadTimes:write"]}
                  permissions={props.user.permissions.rolePermissions}
                  condition={!editAtYard}
                  render={() => {
                    return (
                      <div className={styles.editIcon}>
                        <EditIcon
                          id="AtYard"
                          onClick={() => handleEdit("AtYard")}
                        />
                      </div>
                    );
                  }}
                />
              </Grid>
              <Grid item xs={1} className={styles.editHeader}>
                <span
                  className={
                    !editProducedInTransit
                      ? styles.negativeMarginTitle
                      : styles.title
                  }
                >
                  {props.t("LeadTimes.producedInTransit")}
                </span>
                <HasPermissionTo
                  perform={["leadTimes:write"]}
                  permissions={props.user.permissions.rolePermissions}
                  condition={!editProducedInTransit}
                  render={() => {
                    return (
                      <div className={styles.editIcon}>
                        <EditIcon
                          id="ProducedInTransit"
                          onClick={() => handleEdit("ProducedInTransit")}
                        />
                      </div>
                    );
                  }}
                />
              </Grid>
              <Grid item xs={1} className={styles.editHeader}>
                <span
                  className={
                    !editOrderScheduling
                      ? styles.negativeMarginTitle
                      : styles.title
                  }
                >
                  {props.t("LeadTimes.orderScheduling")}
                </span>
                <HasPermissionTo
                  perform={["leadTimes:write"]}
                  permissions={props.user.permissions.rolePermissions}
                  condition={!editOrderScheduling}
                  render={() => {
                    return (
                      <div className={styles.editIcon}>
                        <EditIcon
                          id="OrderScheduling"
                          onClick={() => handleEdit("OrderScheduling")}
                        />
                      </div>
                    );
                  }}
                />
              </Grid>
            </Grid>
          </div>
        </div>
        <Grid container className={styles.countDiv}>
          <Grid item xs={1} />
          <Grid item xs={2}>
            <span className={styles.count}>{totalChecked} </span>
            model line{totalChecked !== 1 && "s"} selected
          </Grid>
        </Grid>
        <form onSubmit={handleSubmit} ref={formRef}>
          {data.map((dataItem, index) => {
            return (
              <LeadTimesRow
                data={dataItem}
                index={index}
                key={index}
                infoTooltipOpen={infoTooltipOpen}
                infoTooltipColumn={infoTooltipColumn}
                closeInfoTooltip={closeInfoTooltip}
                firstLoad={firstLoad}
                handleKeyDown={handleKeyDown}
                handleChange={handleChange}
                editModeAtYard={editAtYard}
                editModeProducedInTransit={editProducedInTransit}
                editModeOrderScheduling={editOrderScheduling}
                t={props.t}
              />
            );
          })}
          <HasPermissionTo
            perform={["leadTimes:write"]}
            permissions={props.user.permissions.rolePermissions}
            render={() => {
              return (
                <div className={styles.buttonDiv}>
                  {saveSuccess ? (
                    <div className={styles.saveSuccess}>
                      {totalSaved}{" "}
                      {totalSaved !== 1
                        ? props.t("LeadTimes.savedMultiple")
                        : props.t("LeadTimes.savedOne")}
                    </div>
                  ) : (
                    <button
                      type="submit"
                      data-testid="submitBtn"
                      disabled={buttonDisabled}
                    >
                      {props.t("LeadTimes.releaseLeadTimes")}
                    </button>
                  )}
                </div>
              );
            }}
          />
        </form>
      </div>
    </div>
  );
}

export default withTranslation("emp")(LeadTimes);
